import type { FC, ReactNode } from 'react'
import type { ComparisonSummary } from '@/types'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { ToolLogo } from '@/components/directory/tool-logo'

interface ComparisonCardProps {
    comparison: ComparisonSummary
}

export const ComparisonCard: FC<ComparisonCardProps> = ({ comparison }): ReactNode => {
    const { tool_a: a, tool_b: b } = comparison

    return (
        <article className='group relative flex h-full flex-col rounded-2xl border border-border bg-card p-5 transition-colors hover:border-primary/40'>
            <div className='flex items-center'>
                <ToolLogo tool={a} size='md' />
                <span className='-ml-2 inline-flex size-8 items-center justify-center rounded-full border border-border bg-background font-mono text-[10px] font-semibold uppercase text-muted-foreground'>
                    vs
                </span>
                <ToolLogo tool={b} size='md' className='-ml-2' />
            </div>

            <h3 className='mt-4 font-heading text-lg font-semibold leading-snug text-foreground'>
                <Link
                    href={`/compare/${comparison.slug}`}
                    className='after:absolute after:inset-0 focus-visible:outline-none focus-visible:after:rounded-2xl focus-visible:after:outline-2 focus-visible:after:outline-primary'
                >
                    <span data-mdlfy-cms={`tools:${a.id}:name`}>{a.name}</span>
                    {' vs '}
                    <span data-mdlfy-cms={`tools:${b.id}:name`}>{b.name}</span>
                </Link>
            </h3>

            {comparison.summary && (
                <p className='mt-2 flex-1 text-sm leading-relaxed text-muted-foreground' data-mdlfy-cms={`comparisons:${comparison.id}:summary`}>
                    {comparison.summary}
                </p>
            )}

            <span className='mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-[gap] group-hover:gap-2.5' aria-hidden='true'>
                Read the matchup
                <ArrowRight className='size-4' />
            </span>
        </article>
    )
}
